import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Button,
  useColorModeValue,
  Checkbox,
} from '@chakra-ui/react';
import { useSortBy, useTable } from 'react-table';

import { tableColumns, TABLE_COL_IDS } from '../consts/tableConsts';
import { useCampaigns } from '../context/CampaignsContext';
import TableSkeleton from '../../../organizations/components/TableSkeleton';

const CampaignsTable = () => {
  const {
    campaigns,
    loaders: { isTableLoading },
    toggleCampaignSelection,
    selectedCampaigns,
    selectAllCampaigns,
    companiesIdCache,
    handleEditCampaign,
  } = useCampaigns();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: tableColumns,
      data: campaigns,
      disableSortBy: (column) => column.disableSortBy,
    },
    useSortBy,
  );

  const isMultipleSelected = !!selectedCampaigns?.length;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  return (
    <>
      {isTableLoading ? (
        <TableSkeleton />
      ) : (
        <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
          <Thead>
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    pe="10px"
                    key={index}
                    borderColor={borderColor}
                  >
                    {column.id === TABLE_COL_IDS.SELECTION ? (
                      <Checkbox
                        onChange={selectAllCampaigns}
                        isChecked={
                          campaigns.length > 0 && selectedCampaigns.length === campaigns.length
                        }
                        isIndeterminate={
                          selectedCampaigns.length > 0 &&
                          selectedCampaigns.length < campaigns.length
                        }
                        colorScheme="blue"
                      />
                    ) : (
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {column.render('Header')}
                      </Flex>
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell, index) => {
                      let data = '';
                      const cellWidth = cell.column.width;

                      if (cell.column.id === TABLE_COL_IDS.SELECTION) {
                        data = (
                          <Checkbox
                            isChecked={selectedCampaigns.some(
                              (camp) => camp.id === row.original.id,
                            )}
                            onChange={() => toggleCampaignSelection(row.original)}
                            colorScheme="blue"
                          />
                        );
                      } else if (cell.column.id === TABLE_COL_IDS.COMPANY_ID) {
                        const company = Object.values(companiesIdCache)?.length
                          ? companiesIdCache[row.original.companyId]
                          : row.original.companyId;
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="700">
                            {company}
                          </Text>
                        );
                      } else if (cell.column.id === TABLE_COL_IDS.STATUS) {
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="500">
                            {cell.value ? 'פעיל' : 'לא פעיל'}
                          </Text>
                        );
                      } else if (cell.column.id === TABLE_COL_IDS.TYPE) {
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="700">
                            {cell.value}
                          </Text>
                        );
                      } else if (cell.column.id === TABLE_COL_IDS.ACTIONS) {
                        data = (
                          <Flex align="center" gap="8px">
                            <Button
                              variant="green"
                              size="sm"
                              isDisabled={isMultipleSelected}
                              onClick={() => handleEditCampaign(row.original)}
                            >
                              עריכה
                            </Button>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          fontSize={{ sm: '14px' }}
                          width={cellWidth}
                          maxWidth={cellWidth}
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={tableColumns.length} textAlign="center" py={6}>
                  <Text color={textColor} fontSize="sm">
                    אין קמפיינים להצגה
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      )}
    </>
  );
};

export default CampaignsTable;
